'use client';

import { useCallback, useEffect, useRef } from 'react';

const LOAD_MORE_ROOT_MARGIN = '2500px 0px';
const PREFETCH_FROM_END = 10;

export type UseCommentInfiniteScrollParams = {
  isActive: boolean;
  hasNextPage: boolean;
  loading: boolean;
  error: string | null;
  itemCount: number;
  loadMore: () => void;
};

export function useCommentInfiniteScroll({
  isActive,
  hasNextPage,
  loading,
  error,
  itemCount,
  loadMore,
}: UseCommentInfiniteScrollParams) {
  const loadMoreNodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelIndex =
    hasNextPage && itemCount > 0 ? Math.max(0, itemCount - PREFETCH_FROM_END) : -1;

  const observeLoadMoreNode = useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreNodeRef.current = node;
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || !isActive || !hasNextPage || loading || error) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasNextPage && !loading) {
            loadMore();
          }
        },
        { threshold: 0, rootMargin: LOAD_MORE_ROOT_MARGIN },
      );
      observerRef.current.observe(node);
    },
    [error, hasNextPage, isActive, loading, loadMore],
  );

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    observeLoadMoreNode(loadMoreNodeRef.current);
  }, [observeLoadMoreNode, sentinelIndex]);

  return {
    sentinelIndex,
    observeLoadMoreNode,
  };
}
